// see ai-help/duck.ai_2026-05-26_12-13-00.txt
import React from 'react';

export function withRandomIds(SvgComponent) {
  const Wrapped = React.forwardRef(function RandomizedSvg(props, ref) {
    const svgRef = React.useRef(null);
    const idPrefix = React.useMemo(
      () => Math.random().toString(36).substr(2, 9),
      []
    );

    React.useLayoutEffect(() => {
      const node = svgRef.current;
      if (!node) return;

      const idMap = new Map();

      // Step 1: Randomize all IDs
      node.querySelectorAll('[id]').forEach((el) => {
        const newId = `${idPrefix}-${el.id}`;
        idMap.set(el.id, newId);
        el.id = newId;
      });

      // Helper function to replace ID references
      const updateReference = (oldId, newId, value) => {
        return value.replace(new RegExp(`#${oldId}\\b`, 'g'), `#${newId}`);
      };

      // Step 2: Update regular attributes that reference IDs
      const attrs = [
        'href',
        'clip-path',
        'mask',
        'filter',
        'fill',
        'stroke',
        'marker-start',
        'marker-mid',
        'marker-end',
        'background'
      ];

      node.querySelectorAll('*').forEach((el) => {
        // Update regular attributes
        attrs.forEach((attr) => {
          const val = el.getAttribute(attr);
          if (val) {
            let updated = val;
            idMap.forEach((newId, oldId) => {
              updated = updateReference(oldId, newId, updated);
            });
            el.setAttribute(attr, updated);
          }
        });

        // Step 3: Update xlink:href (namespaced attribute)
        const xlinkHref = el.getAttributeNS('http://www.w3.org/1999/xlink', 'href');
        if (xlinkHref) {
          let updated = xlinkHref;
          idMap.forEach((newId, oldId) => {
            updated = updateReference(oldId, newId, updated);
          });
          el.setAttributeNS('http://www.w3.org/1999/xlink', 'href', updated);
        }
      });

      // Step 4: Update <style> tags (CSS inside SVG)
      node.querySelectorAll('style').forEach((styleEl) => {
        let css = styleEl.textContent;
        idMap.forEach((newId, oldId) => {
          css = updateReference(oldId, newId, css);
        });
        styleEl.textContent = css;
      });
    }, [idPrefix]);

    // Forward the ref
    React.useImperativeHandle(ref, () => svgRef.current, []);

    return <SvgComponent ref={svgRef} {...props} />;
  });

  Wrapped.displayName = `withRandomIds(${SvgComponent.displayName || 'SvgComponent'})`;
  return Wrapped;
}
